import { useState, useContext, useRef, useEffect } from 'react';
import api from './api/axiosInstance';
import { UserContext } from './UserContext';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Sidebar from './Sidebar.js';
import { Card, Row, Col, Container, Alert, Modal, Image } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from './Breadcrumb.js';
import CityAddGeneral from './CityAddGeneral';
import CityOfficeDesk from './CityOfficeDesk';
import CitySalary from './CitySalary';
import { isAdminType} from './utils/roleUtils';
import { TicketCountContext } from './TicketCountContext';
import icSuccess from './assets/img/ic-celebration-circle.svg';
import { validateURL } from './utils/validation';

import { MAPBOX_TOKEN } from './config/mapbox';

const CityAdd = () => {
    const salaryRef = useRef(null);
    const { user } = useContext(UserContext);
    const [step, setStep] = useState(1); // Step state
    const navigate = useNavigate(); // Initialize useNavigate
    const { ticketCount } = useContext(TicketCountContext);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [childHasErrors, setChildHasErrors] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        continent: '',
        country: {},
        latitude: '',
        longitude: '',
        citizensNumber: '',
        regionCitizensNumber: '',
        cityId: '',
        url: '',
        languagesSpoken: [],
        cityRegionDescription: '',
        universitiesNumber: '',
        studentsNumber: '',
        officeUrl: '',
        address: '',
        phone: '',
        workingHours: [{ fromDay: '', toDay: '', fromTime: '', toTime: '' }],
        govUrl: '',
    });

    const [errors, setErrors] = useState({}); // State for validation errors

    const [geocodeLoading, setGeocodeLoading] = useState(false);
    const [geocodeError, setGeocodeError] = useState('');
    const lastGeocodedKeyRef = useRef('');

    const geocodeCityCoordinates = async ({
        cityName = formData.name,
        countryName = formData.country?.name,
        force = false,
    } = {}) => {
        const trimmedCity = String(cityName || '').trim();
        const trimmedCountry = String(countryName || '').trim();
        const mapboxToken = MAPBOX_TOKEN;

        if (!trimmedCity || !trimmedCountry) {
            return false;
        }

        if (!mapboxToken) {
            setGeocodeError('Mapbox token is missing.');
            return false;
        }

        const lookupKey = `${trimmedCity.toLowerCase()}__${trimmedCountry.toLowerCase()}`;

        if (!force && lastGeocodedKeyRef.current === lookupKey && formData.latitude && formData.longitude) {
            return true;
        }

        setGeocodeLoading(true);
        setGeocodeError('');

        try {
            const query = encodeURIComponent(`${trimmedCity}, ${trimmedCountry}`);
            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?types=place&limit=1&access_token=${mapboxToken}`
            );

            const data = await response.json();
            const feature = data?.features?.[0];
            const center = feature?.center;

            if (!Array.isArray(center) || center.length < 2) {
                setGeocodeError('Could not detect city coordinates automatically.');
                setFormData(prevData => ({
                    ...prevData,
                    latitude: '',
                    longitude: '',
                }));
                return false;
            }

            const [lng, lat] = center;

            setFormData(prevData => ({
                ...prevData,
                latitude: Number(lat),
                longitude: Number(lng),
            }));

            setErrors(prev => ({
                ...prev,
                latitude: '',
                longitude: '',
            }));

            lastGeocodedKeyRef.current = lookupKey;
            return true;
        } catch (error) {
            console.error('City geocoding failed:', error);
            setGeocodeError('Could not detect city coordinates automatically.');
            return false;
        } finally {
            setGeocodeLoading(false);
        }
    };

    useEffect(() => {
        const trimmedCity = String(formData.name || '').trim();
        const trimmedCountry = String(formData.country?.name || '').trim();

        if (!trimmedCity || !trimmedCountry) {
            setGeocodeError('');
            return;
        }

        const timeout = setTimeout(() => {
            geocodeCityCoordinates({
                cityName: trimmedCity,
                countryName: trimmedCountry,
            });
        }, 700);

        return () => clearTimeout(timeout);
    }, [formData.name, formData.country?.name]);

    const handleChange = (e) => {
        // Support both real events (e.target) and custom objects ({ target: { … } })
        const { name, value } = e.target || e;
        console.log('handleChange called with:', name, value);

        // ✅ helper: only re-validate fields that currently show an error (so errors don't appear while typing)
        const clearErrorIfValid = (fieldName, isValid) => {
            setErrors(prev => {
                if (!prev?.[fieldName]) return prev;      // ✅ if no error shown, don't touch it
                if (!isValid) return prev;                // ✅ keep error until valid
                return { ...prev, [fieldName]: '' };      // ✅ clear when valid
            });
        };

        const v = String(value ?? '');

        // ✅ Step 1 live-clear rules (only clears if that field currently has an error)
        if (name === 'name') clearErrorIfValid('name', v.trim().length > 0);
        if (name === 'continent') clearErrorIfValid('continent', v.trim().length > 0);
        if (name === 'citizensNumber') clearErrorIfValid('citizensNumber', v.trim().length > 0);
        if (name === 'regionCitizensNumber') clearErrorIfValid('regionCitizensNumber', v.trim().length > 0);
        if (name === 'cityId') clearErrorIfValid('cityId', v.trim().length > 0);
        if (name === 'officeUrl') {
            const trimmed = v.trim();
            clearErrorIfValid('officeUrl', !!trimmed && validateURL(trimmed)); // ✅ only clear when valid URL
        }
        if (name === 'address') clearErrorIfValid('address', v.trim().length > 0);
        if (name === 'phone') clearErrorIfValid('phone', v.trim().length > 0);

        // ✅ Country is an object: { id, name }
        if (name === 'country') {
            const nextCountry =
                value && typeof value === 'object'
                    ? { ...value }                            // { id, name }
                    : { ...(formData.country || {}), id: value }; // fallback if somehow a string id sneaks in

            // ✅ clear country error as soon as id exists (only if it was shown)
            clearErrorIfValid('country', !!nextCountry?.id);

            setFormData(prevData => ({
                ...prevData,
                country: nextCountry,
                latitude: '',
                longitude: '',
            }));
            return;
        }

        if (name === 'languagesSpoken') {
            setFormData(prevData => ({
                ...prevData,
                languagesSpoken: Array.isArray(value)
                    ? value.map(v => String(v).trim()).filter(Boolean)
                    : []
            }));
            return;
        }

        if (name === 'latitude') clearErrorIfValid('latitude', v.trim().length > 0);
        if (name === 'longitude') clearErrorIfValid('longitude', v.trim().length > 0);

        //  Default: simple scalar fields (name, continent, etc.)
        setFormData(prevData => ({
            ...prevData,
            [name]: value,
            ...(name === 'name'
                ? {
                    latitude: '',
                    longitude: '',
                }
                : {})
        }));
    };
    
    const validateStep1 = () => {
        const newErrors = {};
    
        if (!formData.name) {
            newErrors.name = 'City name is required.';
        }

        if (!formData.continent || formData.continent.trim() === '') {
            newErrors.continent = 'Continent is required.';
        }

        if (!formData.country || !formData.country.id) {
            newErrors.country = 'Country is required.';
        }

        if (!formData.citizensNumber) {
            newErrors.citizensNumber = 'City citizen number is required.';
        }

        if (!formData.regionCitizensNumber) {
            newErrors.regionCitizensNumber = 'Region citizen number is required.';
        }

        if (!formData.cityId) {
            newErrors.cityId = 'City ID is required.';
        }

        if (!formData.officeUrl || formData.officeUrl.trim() === '') {
            newErrors.officeUrl = 'Citizen office desk URL is required.';
        } else if (!validateURL(formData.officeUrl.trim())) {
            newErrors.officeUrl = 'Please enter a valid URL.';
        }

        if (!formData.address) {
            newErrors.address = 'Address is required.';
        }

        if (!formData.phone) {
            newErrors.phone = 'Phone is required.';
        }

        if (formData.latitude === '' || formData.latitude === null || formData.latitude === undefined) {
            newErrors.latitude = 'Latitude is required.';
        }

        if (formData.longitude === '' || formData.longitude === null || formData.longitude === undefined) {
            newErrors.longitude = 'Longitude is required.';
        }
    
        setErrors(newErrors);
        console.log('Validation Errors:', newErrors);
    
        return Object.keys(newErrors).length === 0;
    };

    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // ✅ force child to validate synchronously (flush debounced URL checks)
        if (step === 2 && salaryRef.current?.validateNow) {
            const ok = await salaryRef.current.validateNow();
            if (!ok) {
                setShowErrorMessage(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
        }

        // ✅ Block if any errors exist (parent errors or child says it has errors)
        const parentHasErrors = !!Object.values(errors || {}).some(Boolean); // ✅
        if (parentHasErrors || childHasErrors) {                             // ✅
            setShowErrorMessage(true);                                       // ✅
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;                                                          // ✅ stop here
        }
        
        const config = {
            headers: {
                'x-auth-token': localStorage.getItem('token'),
                'Content-Type': 'application/json',
            },
        };
    
        // ✅ Destructure out the flat salary fields
        const {
            centralFrom,
            centralTo,
            productionFrom,
            productionTo,
            itFrom,
            itTo,
            internCentralFrom,
            internCentralTo,
            internProductionFrom,
            internProductionTo,
            internItFrom,
            internItTo,
            oneThreeCentralFrom,
            oneThreeCentralTo,
            oneThreeProductionFrom,
            oneThreeProductionTo,
            oneThreeItFrom,
            oneThreeItTo,
            threeCentralFrom,
            threeCentralTo,
            threeProductionFrom,
            threeProductionTo,
            threeItFrom,
            threeItTo,
            upliftCentral,
            upliftProduction,
            upliftIt,
            ...restFormData
        } = formData;

        // ✅ Create the final payload
        const payload = {
            ...restFormData,
            currency: formData.currency,
            govUrl: formData.govUrl,
            studentJobs: [
                {
                    centralFrom: Number(centralFrom) || 0,
                    centralTo: Number(centralTo) || 0,
                    productionFrom: Number(productionFrom) || 0,
                    productionTo: Number(productionTo) || 0,
                    itFrom: Number(itFrom) || 0,
                    itTo: Number(itTo) || 0,
                }
            ],
            internJobs: [
                {
                    internCentralFrom: Number(internCentralFrom) || 0,
                    internCentralTo: Number(internCentralTo) || 0,
                    internProductionFrom: Number(internProductionFrom) || 0,
                    internProductionTo: Number(internProductionTo) || 0,
                    internItFrom: Number(internItFrom) || 0,
                    internItTo: Number(internItTo) || 0,
                }
            ],
            oneThreeJobs: [
                {
                    oneThreeCentralFrom: Number(oneThreeCentralFrom) || 0,
                    oneThreeCentralTo: Number(oneThreeCentralTo) || 0,
                    oneThreeProductionFrom: Number(oneThreeProductionFrom) || 0,
                    oneThreeProductionTo: Number(oneThreeProductionTo) || 0,
                    oneThreeItFrom: Number(oneThreeItFrom) || 0,
                    oneThreeItTo: Number(oneThreeItTo) || 0,
                }
            ],
            threeJobs: [
                {
                    threeCentralFrom: Number(threeCentralFrom) || 0,
                    threeCentralTo: Number(threeCentralTo) || 0,
                    threeProductionFrom: Number(threeProductionFrom) || 0,
                    threeProductionTo: Number(threeProductionTo) || 0,
                    threeItFrom: Number(threeItFrom) || 0,
                    threeItTo: Number(threeItTo) || 0,
                }
            ],
            uplift: [
                {
                    upliftCentral: Number(upliftCentral) || 0,
                    upliftProduction: Number(upliftProduction) || 0,
                    upliftIt: Number(upliftIt) || 0,
                }
            ]
        };

        try {
            const response = await api.put(
                `/api/cities/${formData._id}`,
                payload // ✅ pass plain object, no JSON.stringify
            );
    
            console.log('City updated with salary details:', response.data);
            setShowSuccessModal(true);
        } catch (error) {
            console.error('Error updating city:', error);
            alert('An error occurred while updating the city. Please try again.');
        }
    };
    
    if (!user || !isAdminType(user.role)) {
        return <div>You are not authorized to view this page.</div>;
    }

    const handleNextStep = async () => {
        if (step === 1) {
            const isValid = validateStep1();

            if (!isValid) {
                setShowErrorMessage(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
    
            try {
                const payload = {
                    ...formData,
                    languagesSpoken: Array.isArray(formData.languagesSpoken)
                        ? formData.languagesSpoken.filter(Boolean)
                        : []
                };

                const response = await api.post(
                    `/api/cities`,
                    payload
                );
                console.log('Sending data:', formData);
                console.log('City created:', response.data);
                setFormData((prevData) => ({
                    ...prevData,
                    _id: response.data._id,
                }));
                setShowErrorMessage(false);
                setStep(2); // ✅ Move to Step 2 (Salary)
            } catch (error) {
                console.error('Error creating city:', error);
                alert('An error occurred while creating the city. Please try again.');
            }
        }
    };       
    
    const handlePreviousStep = () => setStep((prevStep) => prevStep - 1);
    
    return (
        <>
            <Sidebar ticketCount={ticketCount} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            {showErrorMessage && (
                                <Alert variant="danger" onClose={() => setShowErrorMessage(false)} dismissible>
                                    <p><strong>Error</strong> - There are items that require your attention</p>
                                </Alert>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        {/* Main Content */}
                        <Col xs={12}>
                            <Card>
                                <Card.Body>
                                    <h1>Add City</h1>
                                    <Form onSubmit={step === 2 ? handleSubmit : undefined}>
                                        {step === 1 && (
                                            <>
                                                <CityAddGeneral
                                                    formData={formData}
                                                    handleChange={handleChange}
                                                    errors={errors}
                                                    geocodeLoading={geocodeLoading}
                                                    geocodeError={geocodeError}
                                                    onRefreshCoordinates={() =>
                                                        geocodeCityCoordinates({
                                                            force: true,
                                                        })
                                                    }
                                                />
                                                <CityOfficeDesk
                                                    formData={formData}
                                                    handleChange={handleChange}
                                                    errors={errors}
                                                    setChildHasErrors={setChildHasErrors}
                                                    onShowErrorMessage={setShowErrorMessage}
                                                />
                                            </>
                                        )}

                                        {step === 2 && 
                                            <CitySalary
                                                formData={formData}
                                                handleChange={handleChange}
                                                errors={errors} 
                                                onValidationChange={setChildHasErrors}
                                                onShowErrorMessage={setShowErrorMessage}
                                                ref={salaryRef}
                                            />
                                        }
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col md={6}>
                            <p>All fields marked with an asterisk (*) are mandatory and must be completed.</p>
                        </Col>
                        <Col className="justify-content-end d-flex align-items-center" md={6}>
                            <Button className="me-3" variant="outline-secondary" onClick={() => navigate('/onboarding-city-list')}>
                                Cancel
                            </Button>
                            {step > 1 && <Button className="me-3" variant="outline-secondary" onClick={handlePreviousStep}>Back</Button>}
                            {step === 1 && <Button variant="primary" onClick={handleNextStep}>Next</Button>}
                            {step === 2 && 
                                <Button
                                    variant="primary"
                                    onClick={async (e) => {
                                    e.preventDefault();
                                    // ✅ local guard before calling parent submit
                                    const ok = await salaryRef.current?.validateNow?.();
                                    if (!ok || Object.values(errors || {}).some(Boolean) || childHasErrors) {
                                        setShowErrorMessage(true);
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                        return;
                                    }
                                    handleSubmit(e);
                                    }}
                                >
                                   Finish
                                </Button>
                            }
                        </Col>
                    </Row>
                </Container>
            </main>

            {/* Success Modal */}
            <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Image
                        src={icSuccess}
                        alt=""
                        className="d-block m-auto mb-4"
                    />
                    <h2 className="text-center mb-3">Congratulations</h2>
                    <p className="text-center">You have successfully added a new city to aBROADIFY</p>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center">
                    <Button variant="primary" onClick={() => navigate('/onboarding-city-list')}>
                        Go to Cities
                    </Button>
                </Modal.Footer>
            </Modal>;
        </>
    );
};

export default CityAdd;
